/**
 * useListKeyNav — 떠 있는 판 안 후보 목록의 키보드(설계 FE-20260912 §W02·§W08).
 *
 * 초점은 **검색 입력에 둔 채** 활성 후보만 옮긴다(aria-activedescendant). 글자를 치다 ↓ 로 내려가 고르고 다시 치는 흐름이
 * 한 입력 안에서 끊기지 않게 — 후보마다 초점을 옮기면 글자를 더 치려고 입력으로 돌아가야 한다.
 *
 * Enter 는 활성 후보를 **고르거나 풀기만** 한다. 적용은 [적용] 버튼의 몫이다 — 한 키가 두 동작을 하지 않게
 * (설계 §W02 「후보에 초점이 있을 때 Enter 는 선택만」). 목록이 비어 있어도 Enter 는 삼킨다(판 밖으로 새지 않게).
 * 한글 조합 중의 키는 모두 흘려보낸다 — 조합을 확정하는 Enter 를 가로채면 마지막 글자가 먹힌다(ConceptFilterInput 과 같은 규칙).
 * Escape 는 여기서 잡지 않는다 — FloatingPanel 이 판 onKeyDown 에서 받아 닫는다.
 */
import { useEffect, useId, useRef, useState } from 'react';

/** 조합 중인 키인가 — 조합 중에는 브라우저가 keyCode 229 를 붙이기도 한다 */
export const isComposingKey = (e) => !!(e?.nativeEvent?.isComposing || e?.isComposing || e?.keyCode === 229);

/**
 * @param count     지금 보이는 후보 수
 * @param onPick    (i) => void — Enter 로 활성 후보를 고르거나 푼다
 * @param resetKey  목록이 바뀌는 열쇠(검색어 등) — 바뀌면 활성 후보를 맨 위로 되돌린다
 */
export function useListKeyNav({ count, onPick, resetKey = '' }) {
  const baseId = useId();
  const listRef = useRef(null);
  // 열쇠가 바뀐 뒤의 첫 렌더에서 곧바로 0 으로 읽히게 한다 — effect 로 되돌리면 한 번은 옛 번호가 보인다
  const [state, setState] = useState({ key: resetKey, index: 0 });
  const raw = state.key === resetKey ? state.index : 0;
  const active = count > 0 ? Math.min(Math.max(raw, 0), count - 1) : -1;
  const optionId = (i) => `${baseId}-opt-${i}`;
  const listId = `${baseId}-list`;
  const setActive = (i) => setState({ key: resetKey, index: i });

  // 활성 후보가 목록 밖으로 나가면 목록만 굴린다 — scrollIntoView 는 창까지 굴려 FloatingPanel 을 닫을 수 있다
  useEffect(() => {
    const list = listRef.current;
    if (!list || active < 0) return;
    const el = document.getElementById(optionId(active));
    if (!el || !list.contains(el)) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (e) => {
    if (isComposingKey(e)) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (count > 0) setActive(Math.min(active + 1, count - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (count > 0) setActive(Math.max(active - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0) onPick(active);
    }
  };

  return { active, setActive, onKeyDown, optionId, listId, listRef };
}

/**
 * 판이 자리를 잡은 뒤 입력에 초점 — FloatingPanel 은 첫 렌더에서 판을 숨겨 두고(visibility:hidden) 자리를 잰 뒤 보인다.
 * 숨은 요소에는 초점이 붙지 않으므로 한 틱 뒤에 준다. deps 가 바뀌면(탭 전환) 다시 준다.
 */
export function useFocusSoon(ref, deps = []) {
  useEffect(() => {
    const t = setTimeout(() => {
      const el = ref.current;
      if (el && typeof el.focus === 'function') el.focus();
    }, 0);
    return () => clearTimeout(t);
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}
